import { useInfiniteQuery } from "@tanstack/react-query";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "@/design-system/Button";
import { Column, Columns } from "@/design-system/Columns";
import { ContentBlock } from "@/design-system/ContentBlock";
import { ModalBodySpinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { Tabs } from "@/design-system/Tabs";
import type { ChatsRouteParams } from "@/helpers/chats";
import { buildDeepLinkUrl, type SupportedDeepLinkEntityType } from "@/helpers/deep-link";
import { useProject } from "@/hooks/Network/useProject";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useProjectAppBrandingConfig } from "@/providers/ProjectAppBrandingConfigProvider";
import { useChatQueries } from "@/queries/chats/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { ChatPreview } from "../ChatPreview/ChatPreview";
import { EntityPreview } from "../EntityPreview/EntityPreview";
import { useFlashToast } from "../FlashToast/FlashToast";
import { MessageChatCircleIcon } from "../Icons/MessageChatCircleIcon";
import { List } from "../List/List";
import type { ModalBaseProps } from "../Modal/Modal";
import { Modal } from "../Modal/Modal";
import { SearchInput } from "../SearchInput/SearchInput";
import { User } from "../User/User";

type ShareToChatModalTabs = "chats" | "neighbours";
type ShareToChatModalProps = ModalBaseProps & {
  entityId: string;
  entityType: SupportedDeepLinkEntityType;
  otherIds?: Record<string, string>;
};

export function ShareToChatModal({
  entityId,
  entityType,
  otherIds,
  isOpened,
  onOpenChange,
}: ShareToChatModalProps): React.ReactNode {
  const defaultActiveTab: ShareToChatModalTabs = "chats";
  const [activeTab, setActiveTab] = useState<ShareToChatModalTabs>(defaultActiveTab);
  const [search, setSearch] = useState("");

  const sessionUser = useSessionUser();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const project = useProject();
  const showFlashToast = useFlashToast();
  const debouncedQuery = useDebounceValue(search.toLowerCase().trim());
  const { appBrandingConfig } = useProjectAppBrandingConfig();

  const chatQueries = useChatQueries();
  const userQueries = useUserQueries();

  const {
    data: chatsData,
    hasNextPage: hasMoreChats,
    fetchNextPage: fetchMoreChats,
    isFetching: isFetchingChats,
    isFetchingNextPage: isFetchingMoreChats,
    isError: isErrorChats,
  } = useInfiniteQuery({
    ...chatQueries.listInfinite(
      debouncedQuery
        ? {
            Search: debouncedQuery,
          }
        : undefined,
    ),
    enabled: isOpened,
  });
  const {
    data: usersData,
    hasNextPage: hasMoreUsers,
    fetchNextPage: fetchMoreUsers,
    isFetchingNextPage: isFetchingMoreUsers,
    isError: isErrorUsers,
  } = useInfiniteQuery({
    ...userQueries.listInfinite({
      Search: debouncedQuery,
      SortBy: "firstName",
      SortDescending: false,
    }),
    enabled: isOpened,
  });

  const refLoader = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (activeTab === "chats" && !isFetchingMoreChats && hasMoreChats) {
        void fetchMoreChats();
      }

      if (activeTab === "neighbours" && !isFetchingMoreUsers && hasMoreUsers) {
        void fetchMoreUsers();
      }
    }, [
      activeTab,
      fetchMoreChats,
      hasMoreChats,
      isFetchingMoreChats,
      fetchMoreUsers,
      hasMoreUsers,
      isFetchingMoreUsers,
    ]),
  });

  const handleShare = (chatId: string, isGroupChat: boolean) => {
    let deepLinkUrl: string;
    let chatDetailsRoute: string;

    if (entityType === "document") {
      deepLinkUrl = buildDeepLinkUrl({
        baseUrl: appBrandingConfig.deepLink,
        payload: {
          entityType,
          entityId,
          projectId: project.id,
          folderId: otherIds?.folderId || "",
        },
      });
    } else if (entityType === "group") {
      deepLinkUrl = buildDeepLinkUrl({
        baseUrl: appBrandingConfig.deepLink,
        payload: {
          entityType,
          entityId,
          projectId: project.id,
          groupTypeId: otherIds?.groupTypeId || "",
        },
      });
    } else {
      deepLinkUrl = buildDeepLinkUrl({
        baseUrl: appBrandingConfig.deepLink,
        payload: {
          entityType,
          entityId,
          projectId: project.id,
        },
      });
    }

    if (isGroupChat) {
      chatDetailsRoute = routes.chats.details({
        slug: project.slug,
        id: chatId,
      });
    } else {
      chatDetailsRoute = routes.users.details({
        slug: project.slug,
        id: chatId,
      });
    }

    const searchParams = new URLSearchParams({
      prefilledReply: deepLinkUrl,
    } satisfies ChatsRouteParams["details"]);

    void navigate(`${chatDetailsRoute}?${searchParams.toString()}`);

    onOpenChange(false);
  };

  const chats = useMemo(() => chatsData?.pages.flatMap((page) => page.items) ?? [], [chatsData]);
  const users = useMemo(
    () => usersData?.pages.flatMap((page) => page.items.filter((x) => x.id !== sessionUser.id)) || [],
    [usersData, sessionUser.id],
  );

  const isError = isErrorChats || isErrorUsers;
  useEffect(() => {
    if (isError) {
      onOpenChange(false);
      showFlashToast({
        variant: "alert",
        message: t("component.share-to-chat-modal.notification.error"),
      });
    }
  }, [isError, onOpenChange, showFlashToast, t]);

  return (
    <Modal.Root
      size="md"
      height={640}
      title={t("component.share-to-chat-modal.title")}
      data-testid="share-via-chat-modal"
      isPending={isError}
      onExitComplete={() => setActiveTab(defaultActiveTab)}
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        <Stack gap="md">
          <ContentBlock width="flow" align="left">
            <EntityPreview entityId={entityId} entityType={entityType} otherIds={otherIds || {}} readOnly />
          </ContentBlock>
          <Tabs.Root
            activeTab={activeTab}
            onSelectTab={setActiveTab}
            tabs={[
              {
                label: t("component.share-to-chat-modal.tabs.chats"),
                value: "chats",
                "data-testid": "chats-tab",
              },
              {
                label: t("component.share-to-chat-modal.tabs.neighbours"),
                value: "neighbours",
                "data-testid": "neighbours-tab",
              },
            ]}
          >
            <ContentBlock>
              <Stack gap="md">
                <SearchInput
                  width="full"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("component.share-to-chat-modal.search.placeholder")}
                />
                <Tabs.Content value="chats" isLoading={isFetchingChats}>
                  {chats.length === 0 && (
                    <p className="p-space-md text-center text-text-secondary">{t("common.label.no-result")}</p>
                  )}
                  <List getKey={(chat) => chat.id} items={chats}>
                    {(chat) => {
                      const isGroupChat = chat.chatType === "group";
                      const chatId = isGroupChat ? chat.id : chat.privateChatInfo?.receiver.id;

                      if (chat.privateChatInfo?.receiver.deletedAt || !chatId) {
                        return null;
                      }

                      return (
                        <Columns gap="md" align="center">
                          <Column>
                            <ChatPreview chat={chat} />
                          </Column>
                          <Column width="content">
                            <Button
                              variant="subtle"
                              icon={<MessageChatCircleIcon />}
                              onClick={() => handleShare(chatId, isGroupChat)}
                            >
                              {t("common.action.share")}
                            </Button>
                          </Column>
                        </Columns>
                      );
                    }}
                  </List>
                  {hasMoreChats && (
                    <div ref={refLoader}>
                      <ModalBodySpinner />
                    </div>
                  )}
                </Tabs.Content>
                <Tabs.Content value="neighbours">
                  {users.length === 0 && (
                    <p className="p-space-md text-center text-text-secondary">{t("common.label.no-result")}</p>
                  )}
                  <List getKey={(user) => user.id} items={users}>
                    {(user) => (
                      <Columns gap="md" align="center">
                        <Column>
                          <User user={user} renderSubtitle={(user) => user.locatedAt} shouldNavigate={false} />
                        </Column>
                        <Column width="content">
                          <Button
                            variant="subtle"
                            icon={<MessageChatCircleIcon />}
                            onClick={() => handleShare(user.id, false)}
                            data-testid="share-btn"
                          >
                            {t("common.action.share")}
                          </Button>
                        </Column>
                      </Columns>
                    )}
                  </List>
                  {hasMoreUsers && (
                    <div ref={refLoader}>
                      <ModalBodySpinner />
                    </div>
                  )}
                </Tabs.Content>
              </Stack>
            </ContentBlock>
          </Tabs.Root>
        </Stack>
      </Modal.Body>
    </Modal.Root>
  );
}
